import { useCallback, useEffect, useRef, useState } from 'react';
import { checkLookup, getDatasetColumns } from 'src/components/star-grid/datasets/services/datasetService';
import type { DatasetColumnInfo, DatasetFixedFilter, DatasetLookup, LookupCheckResult } from 'src/components/star-grid/datasets/datasetTypes';
import { errorMessage } from 'src/components/star-grid/utils/errorMessage';

export interface LookupDraftState {
  readonly column: string;
  readonly tableRef: string;
  readonly key: string;
  readonly attributes: string[];
  readonly setColumn: (value: string) => void;
  readonly setTableRef: (value: string) => void;
  readonly setKey: (value: string) => void;
  readonly setAttributes: (value: string[]) => void;
  readonly columns: DatasetColumnInfo[];
  readonly columnsLoading: boolean;
  /** Why the lookup table's columns could not be read, e.g. an Andes table StarGrid is not granted. */
  readonly columnsError: string | null;
  readonly check: LookupCheckResult | null;
  readonly checking: boolean;
  readonly checkError: string | null;
  readonly canCheck: boolean;
  readonly runCheck: () => void;
  readonly canAdd: boolean;
  readonly build: () => DatasetLookup | null;
  readonly prefill: (lookup: DatasetLookup) => void;
}

/** The not-yet-added lookup row's form state: the lookup table's columns, the join check, and the draft fields. */
export function useLookupDraft(factRef: string, fixedFilters: readonly DatasetFixedFilter[] = [], roleArn?: string): LookupDraftState {
  const [column, setColumn] = useState('');
  const [tableRef, setTableRefState] = useState('');
  const [key, setKey] = useState('');
  const [attributes, setAttributes] = useState<string[]>([]);
  const [columns, setColumns] = useState<DatasetColumnInfo[]>([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [columnsError, setColumnsError] = useState<string | null>(null);
  const [check, setCheck] = useState<LookupCheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);

  const columnsController = useRef<AbortController | null>(null);
  const checkController = useRef<AbortController | null>(null);

  useEffect(() => {
    columnsController.current?.abort();
    setColumnsError(null);
    if (!tableRef) {
      setColumns([]);
      return;
    }
    const controller = new AbortController();
    columnsController.current = controller;
    setColumnsLoading(true);
    getDatasetColumns(tableRef, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) setColumns(response.columns);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setColumns([]);
        setColumnsError(errorMessage(caught));
      })
      .finally(() => {
        if (!controller.signal.aborted) setColumnsLoading(false);
      });
    return () => controller.abort();
  }, [tableRef]);

  // A new table invalidates the key, attributes, and any check run against the old one.
  const setTableRef = useCallback((value: string) => {
    setTableRefState(value);
    setKey('');
    setAttributes([]);
    setCheck(null);
    setCheckError(null);
  }, []);

  const canCheck = Boolean(column && tableRef && key && factRef);
  const canAdd = canCheck && check !== null;

  const build = useCallback(
    (): DatasetLookup | null => (column && tableRef && key ? { column, table_ref: tableRef, key, attributes } : null),
    [column, tableRef, key, attributes]
  );

  const runCheck = useCallback(() => {
    const lookup = build();
    if (!lookup || !factRef) return;
    checkController.current?.abort();
    const controller = new AbortController();
    checkController.current = controller;
    setChecking(true);
    setCheckError(null);
    checkLookup(factRef, lookup, controller.signal, fixedFilters, roleArn)
      .then((result) => {
        if (!controller.signal.aborted) setCheck(result);
      })
      .catch((caught: unknown) => {
        if (!controller.signal.aborted) setCheckError(errorMessage(caught));
      })
      .finally(() => {
        if (!controller.signal.aborted) setChecking(false);
      });
  }, [build, factRef, fixedFilters, roleArn]);

  const prefill = useCallback((lookup: DatasetLookup) => {
    setColumn(lookup.column);
    setTableRefState(lookup.table_ref);
    setKey(lookup.key);
    setAttributes([...lookup.attributes]);
    setCheck(null);
    setCheckError(null);
  }, []);

  return {
    column,
    tableRef,
    key,
    attributes,
    setColumn,
    setTableRef,
    setKey: (value) => {
      setKey(value);
      setCheck(null);
    },
    setAttributes: (value) => {
      setAttributes(value);
      setCheck(null);
    },
    columns,
    columnsLoading,
    columnsError,
    check,
    checking,
    checkError,
    canCheck,
    runCheck,
    canAdd,
    build,
    prefill
  };
}
